/**
 * H2 — the asset journey timeline: workflow transitions render as
 * from→to state chips (server-resolved colors), shipment scans show the
 * location hop + a chip to the shipment (and its order when linked),
 * legacy rows fall back to the classic status label. Newest first.
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, PackageCheck, Send, History, GitBranch } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { getAssetStatusLabel } from '@/config/tenant';
import {
  useAssetTrace,
  type AssetTraceEvent,
  type TraceState,
} from '@/api/hooks/useAssetTrace';

/** Server-provided Tailwind color token → badge classes. */
function stateBadgeClass(color: string | null): string {
  const map: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-700',
    emerald: 'bg-emerald-100 text-emerald-700',
    green: 'bg-green-100 text-green-700',
    violet: 'bg-violet-100 text-violet-700',
    amber: 'bg-amber-100 text-amber-700',
    orange: 'bg-orange-100 text-orange-700',
    cyan: 'bg-cyan-100 text-cyan-700',
    red: 'bg-red-100 text-red-700',
    indigo: 'bg-indigo-100 text-indigo-700',
    gray: 'bg-gray-100 text-gray-700',
  };
  return map[color ?? ''] ?? 'bg-slate-100 text-slate-700';
}

function StateChip({ state }: { state: TraceState }) {
  return (
    <Badge className={stateBadgeClass(state.uiBadgeColor)}>
      {state.name ?? state.code}
    </Badge>
  );
}

function kindIcon(kind: AssetTraceEvent['kind']) {
  switch (kind) {
    case 'SHIPMENT_DISPATCH': return <Send className="h-4 w-4 text-amber-600" />;
    case 'SHIPMENT_RECEIVE': return <PackageCheck className="h-4 w-4 text-emerald-600" />;
    case 'LEGACY_STATUS': return <History className="h-4 w-4 text-muted-foreground" />;
    default: return <GitBranch className="h-4 w-4 text-primary" />;
  }
}

function TraceEventRow({ e }: { e: AssetTraceEvent }) {
  const { t } = useTranslation('assets');
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center pt-1">
        {kindIcon(e.kind)}
        <div className="mt-1 h-full w-px bg-border" />
      </div>
      <div className="pb-4 space-y-1 min-w-0">
        {/* Headline */}
        {e.kind === 'LEGACY_STATUS' ? (
          <p className="text-sm">
            {e.description || getAssetStatusLabel(e.legacyStatus ?? 0)}
          </p>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5 text-sm">
            <span className="font-medium">
              {e.transitionName ?? e.transitionCode ?? t('trace.scan')}
            </span>
            {e.fromState && e.toState && (
              <span className="flex items-center gap-1">
                <StateChip state={e.fromState} />
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <StateChip state={e.toState} />
              </span>
            )}
          </div>
        )}

        {/* Shipment hop + chips */}
        {e.shipment && (
          <p className="text-xs text-muted-foreground">
            {e.shipment.sourceLocation ?? '?'}
            {' → '}
            {e.shipment.destinationLocation ?? '?'}
            {e.shipment.destinationClient && ` · ${e.shipment.destinationClient}`}
            {' · '}
            <Link to={`/shipments/${e.shipment.id}`} className="text-primary underline">
              {t('trace.shipment')} #{e.shipment.id}
            </Link>
            {e.shipment.orderCode && (
              <>
                {' · '}
                <span className="font-mono">
                  {e.shipment.orderCode}
                  {e.shipment.orderExternalRef ? ` (${e.shipment.orderExternalRef})` : ''}
                </span>
              </>
            )}
          </p>
        )}

        {/* Form payload */}
        {e.formData && Object.keys(e.formData).length > 0 && (
          <p className="text-xs text-muted-foreground font-mono truncate">
            {Object.entries(e.formData)
              .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
              .join(' · ')}
          </p>
        )}

        {/* Timestamp + user */}
        <p className="text-xs text-muted-foreground">
          {new Date(e.occurredAt).toLocaleString()}
          {e.userName ? ` · ${e.userName}` : e.userId != null ? ` · #${e.userId}` : ''}
        </p>
      </div>
    </div>
  );
}

export function AssetTraceTimeline({
  assetId,
  pageSize = 50,
  maxHeight = 420,
}: {
  assetId: string;
  pageSize?: number;
  maxHeight?: number;
}) {
  const { t } = useTranslation('assets');
  const { data, isLoading } = useAssetTrace(assetId, pageSize);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 w-full" />)}
      </div>
    );
  }
  if (!data || data.events.length === 0) {
    return <p className="py-4 text-center text-sm text-muted-foreground">{t('trace.empty')}</p>;
  }

  return (
    <ScrollArea style={{ maxHeight }} className="pr-3">
      <div>
        {data.events.map((e, i) => <TraceEventRow key={i} e={e} />)}
      </div>
      {data.total > data.events.length && (
        <p className="text-xs text-muted-foreground pb-2">
          {t('trace.showing', { shown: data.events.length, total: data.total })}
        </p>
      )}
    </ScrollArea>
  );
}
